import React, { useCallback, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { connect, useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useParams as useV6Params } from 'react-router-dom-v5-compat';

import { ModalFrameworkMethodsPropType } from 'ui-components/prop-types/modal-framework-methods';
import { GuidService } from 'ui-components/services/guid-service';

import { ShipmentDetailsModalContainer } from './shipment-details-modal-container';
import asModal from '../../../components/as-modal';
import Global from '../../../store/global-selectors';
import {
  openShipmentDetailModal,
  closedShipmentDetailModal,
  getShipmentDetailsSuccess,
} from '../../../store/shipments/shipment-details/shipments.details.actions';

const mapDispatchToProps = {
  onClose: closedShipmentDetailModal,
  loadDetails: openShipmentDetailModal,
  loadSuccess: getShipmentDetailsSuccess,
};

const ShipmentModalLoaderComponent = ({
  fulfillmentPlanId,
  loadDetails,
  loadSuccess,
  modalProperties: { modalId },
  shipmentDetailModalId,
  modalFrameworkMethods,
}) => {
  const hasShipmentWithFulfillmentPlanId = useSelector(
    (state) => Global.selectEntitiesFulfillmentPlanById(state, { fulfillmentPlanId })?.fulfillmentPlanId === fulfillmentPlanId,
  );

  useEffect(() => {
    if (fulfillmentPlanId && !hasShipmentWithFulfillmentPlanId) {
      loadDetails(fulfillmentPlanId, modalId, shipmentDetailModalId);
    } else if (fulfillmentPlanId && hasShipmentWithFulfillmentPlanId) {
      loadSuccess(fulfillmentPlanId, shipmentDetailModalId);
    }
  }, [fulfillmentPlanId, hasShipmentWithFulfillmentPlanId, loadDetails, loadSuccess, modalId, shipmentDetailModalId]);

  return (
    <ShipmentDetailsModalContainer
      fulfillmentPlanId={fulfillmentPlanId}
      modalFrameworkMethods={modalFrameworkMethods}
      shipmentDetailModalId={shipmentDetailModalId}
    />
  );
};

ShipmentModalLoaderComponent.propTypes = {
  fulfillmentPlanId: PropTypes.string.isRequired,
  loadDetails: PropTypes.func.isRequired,
  loadSuccess: PropTypes.func.isRequired,
  modalProperties: PropTypes.shape({
    modalId: PropTypes.string,
  }).isRequired,
  shipmentDetailModalId: PropTypes.string.isRequired,
  modalFrameworkMethods: ModalFrameworkMethodsPropType.isRequired,
};

const ModalLoaderModal = asModal(ShipmentModalLoaderComponent);

const ModalLoaderModalWrapper = ({ onClose: onCloseProp, loadDetails, loadSuccess }) => {
  const { fulfillmentPlanId } = useParams();
  const shipmentDetailModalId = useMemo(() => GuidService.newGuid(), []);
  const onClose = useCallback(() => {
    onCloseProp(shipmentDetailModalId);
  }, [onCloseProp, shipmentDetailModalId]);

  return (
    <ModalLoaderModal
      fulfillmentPlanId={fulfillmentPlanId}
      loadDetails={loadDetails}
      loadSuccess={loadSuccess}
      onClose={onClose}
      shipmentDetailModalId={shipmentDetailModalId}
    />
  );
};

ModalLoaderModalWrapper.propTypes = {
  onClose: PropTypes.func.isRequired,
  loadDetails: PropTypes.func.isRequired,
  loadSuccess: PropTypes.func.isRequired,
  fulfillmentPlanId: PropTypes.string,
};

export const V6RouteModalLoaderModalWrapper = () => {
  const dispatch = useDispatch();
  const { fulfillmentPlanId } = useV6Params();
  const shipmentDetailModalId = useMemo(() => GuidService.newGuid(), []);

  const onClose = useCallback(() => {
    dispatch(closedShipmentDetailModal(shipmentDetailModalId));
  }, [dispatch, shipmentDetailModalId]);

  const loadDetails = useCallback(
    (...args) => {
      dispatch(openShipmentDetailModal(...args));
    },
    [dispatch],
  );

  const loadSuccess = useCallback(
    (...args) => {
      dispatch(getShipmentDetailsSuccess(...args));
    },
    [dispatch],
  );

  return (
    <ModalLoaderModal
      fulfillmentPlanId={fulfillmentPlanId}
      loadDetails={loadDetails}
      loadSuccess={loadSuccess}
      onClose={onClose}
      shipmentDetailModalId={shipmentDetailModalId}
    />
  );
};

export const ShipmentModalLoader = connect(null, mapDispatchToProps)(ModalLoaderModalWrapper);
